import { useState, useCallback, useRef, useEffect } from "react";
import { Text, Stack, Textarea, Button, Group, Chip } from "@mantine/core";
import { LOW_RATING_FEEDBACK_REASONS } from "@/configs/feedbackReasons";
import { Analytics } from "@/services/Analytics";

interface PlaylistFeedbackFormProps {
  /** Current rating that triggered the feedback form */
  rating: number;
  /** Playlist ID for tracking */
  playlistId?: string;
  /** Callback when form is submitted */
  onSubmit?: (data: FeedbackFormData) => void;
  /** Callback when form is cancelled */
  onCancel?: () => void;
  /** Whether the form is in loading state */
  loading?: boolean;
}

export interface FeedbackFormData {
  rating: number;
  reasons: string[];
  additionalComments: string;
  playlistId?: string;
}

export function PlaylistFeedbackForm({
  rating,
  playlistId,
  onSubmit,
  onCancel,
  loading = false,
}: PlaylistFeedbackFormProps) {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [additionalComments, setAdditionalComments] = useState("");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Debounced handler for textarea changes
  const handleDebouncedCommentChange = useCallback(
    (value: string) => {
      // Track feedback comment input with debouncing
      Analytics.trackEvent("feedback", {
        action: "additional_comments",
        label: value,
        value: rating,
        context: "playlist-conclusion",
        playlistId,
      });
    },
    [playlistId, rating],
  );

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newValue = event.currentTarget.value;
    setAdditionalComments(newValue);

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debounced trackingxw
    debounceTimeoutRef.current = setTimeout(() => {
      handleDebouncedCommentChange(newValue);
    }, 500); // 500ms debounce for textarea
  };

  const handleReasonToggle = (reasonId: string) => {
    setSelectedReasons((prev) => {
      const newReasons = prev.includes(reasonId)
        ? prev.filter((id) => id !== reasonId)
        : [...prev, reasonId];

      // Track feedback reason selection immediately
      Analytics.trackEvent("feedback", {
        action: "reason_selection",
        label: reasonId,
        value: rating,
        context: "playlist-conclusion",
        playlistId,
      });

      return newReasons;
    });
  };

  const handleSubmit = () => {
    const formData: FeedbackFormData = {
      rating,
      reasons: selectedReasons,
      additionalComments: additionalComments.trim(),
      playlistId,
    };

    // Track feedback form submission
    Analytics.trackEvent("feedback", {
      action: "form_submitted",
      label: "feedback_form",
      value: rating,
      context: `reasons_${selectedReasons.length}_comments_${formData.additionalComments.length > 0 ? "yes" : "no"}`,
      playlistId,
    });

    // TODO: Implement actual submission logic
    console.log("Feedback form submitted:", formData);

    if (onSubmit) {
      onSubmit(formData);
    }
  };

  const handleCancel = () => {
    // Track feedback form cancellation
    Analytics.trackEvent("feedback", {
      action: "form_cancelled",
      label: "feedback_form",
      value: rating,
      context: `reasons_${selectedReasons.length}_comments_${additionalComments.trim().length > 0 ? "yes" : "no"}`,
      playlistId,
    });

    // Reset form state
    setSelectedReasons([]);
    setAdditionalComments("");

    // Clear any pending debounced tracking
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // TODO: Implement cancel logic (analytics, etc.)
    console.log("Feedback form cancelled");

    if (onCancel) {
      onCancel();
    }
  };

  const isFormValid =
    selectedReasons.length > 0 || additionalComments.trim().length > 0;

  return (
    <Stack gap="lg">
      <Stack gap="md">
        <Text size="md" fw={500}>
          Why wasn't this playlist super helpful? <i>Select all that apply:</i>
        </Text>

        <Group gap="xs">
          {LOW_RATING_FEEDBACK_REASONS.map((reason) => (
            <Chip
              key={reason.id}
              checked={selectedReasons.includes(reason.id)}
              onChange={() => handleReasonToggle(reason.id)}
              variant="outline"
              size="sm"
            >
              {reason.label}
            </Chip>
          ))}
        </Group>
      </Stack>

      <Textarea
        placeholder="Tell us more about your experience..."
        value={additionalComments}
        onChange={handleCommentChange}
        minRows={3}
        maxRows={5}
      />

      <Group justify="flex-end" mt="sm">
        <Button variant="subtle" onClick={handleCancel} disabled={loading}>
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          disabled={!isFormValid}
        >
          Share Feedback
        </Button>
      </Group>
    </Stack>
  );
}
